import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Modal,
  Paper,
  Fade,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import toast from "react-hot-toast";
import { URL } from "../config";
import AddOffersModal from "./AddOffers";
import { useOutletContext } from "react-router-dom";


const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-GB"); 

};
const OfferList = () => {
  const { setLoading } = useOutletContext();

  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editOffer, setEditOffer] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
    const [offerTypes, setOfferTypes] = useState([]);

  useEffect(() => {
    const fetchOfferTypes = async () => {
      try {
        const res = await axios.get(`${URL}/offertypes`);
      
        setOfferTypes(res.data);
      } catch (err) {
        console.error("Failed to fetch offer types:", err);
      }
    };

    fetchOfferTypes();
  }, []);


  const fetchOffers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${URL}/offers`);
      const formatted = res.data.map((o) => ({
        ...o,
        companyLogoUrl: o.companyLogo ? `${URL}${o.companyLogo}` : "",
        offerImageUrl: o.offerImage ? `${URL}${o.offerImage}` : "",
      }));
      setDataSource(formatted);
    } catch (err) {
      toast.error("Failed to fetch offers");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOffers();
  }, []);


  const handleEdit = (offer) => {
    setEditOffer(offer);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    try {
      await axios.delete(`${URL}/offers/${id}`);
      toast.success("Offer deleted");
      fetchOffers();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleOpenImage = (img) => {
    setSelectedImage(img);
    setOpenImageModal(true);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
    setOpenImageModal(false);
  };

  const handleDownloadExcel = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Offer List");

  worksheet.columns = [
    { header: "Sr No.", key: "offerId", width: 10 },
    { header: "Company", key: "companyName", width: 25 },
    { header: "Company URL", key: "companyUrl", width: 30 },
    { header: "Title", key: "offerTitle", width: 30 },
    { header: "Short Description", key: "shortDescription", width: 35 },
    { header: "Coupon Code", key: "couponCode", width: 20 },
    { header: "Offer Details", key: "offerDetails", width: 40 },
    { header: "Start Date", key: "startdate", width: 15 },
    { header: "End Date", key: "endDate", width: 15 },
  ];

  dataSource.forEach((item) => {
    worksheet.addRow({
      offerId: item.offerId,
      companyName: item.companyName,
      companyUrl: item.companyUrl || "",
      offerTitle: item.offerTitle,
      shortDescription: item.shortDescription,
      couponCode: item.couponCode,
      offerDetails: item.offerDetails,
      startdate: formatDate(item.startdate), 
      endDate: formatDate(item.endDate),     
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "Offer_List.xlsx");
};
const handleDownloadPDF = () => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(18);
  doc.text("Offer List", 40, 40);

  const head = [[
    "Sr No.",
    "Company",
    "Company URL",
    "Title",
    "Short Description",
    "Coupon",
    "Offer Details",
    "Start Date",
    "End Date",
  ]];

  const body = dataSource.map((item) => [
    item.offerId,
    item.companyName,
    item.companyUrl || "", 
    item.offerTitle,
    item.shortDescription,
    item.couponCode,
    item.offerDetails,
    formatDate(item.startdate), 
    formatDate(item.endDate),   
  ]);

  autoTable(doc, {
    startY: 60,
    head,
    body,
    headStyles: { fillColor: [23, 66, 83], textColor: "#fff" },
    styles: { fontSize: 10, cellPadding: 6 },
    theme: "grid",
  });

  doc.save("Offer_List.pdf");
};
const handlePrint = () => {
  const tableRows = dataSource
    .map(
      (item) => `
      <tr>
        <td>${item.offerId}</td>
        <td>${item.companyName}</td>
        <td>${item.companyUrl || ""}</td>
        <td>${item.offerTitle}</td>
        <td>${item.shortDescription}</td>
        <td>${item.couponCode}</td>
        <td>${item.offerDetails}</td>
        <td>${formatDate(item.startdate)}</td>
        <td>${formatDate(item.endDate)}</td>
      </tr>
    `
    )
    .join("");

  const style = `
    <style>
      @media print {
        @page { size: landscape; margin: 18mm; }
        body { -webkit-print-color-adjust: exact !important; }
      }
      table { width: 100%; border-collapse: collapse; font-size: 14px; }
      th { background: #174253; color: #fff; padding: 10px; text-align: left; border-bottom: 2px solid #174253; }
      td { padding: 10px; border-top: 1px solid #dcdcdc; border-bottom: 1px solid #dcdcdc; }
      tr:nth-child(even) td { background: #f5f5f5; }
      h1 { text-align: center; color: #174253; margin-bottom: 20px; font-size: 22px; }
    </style>`;

  const html = `
    <html>
      <head><title>Offer List</title>${style}</head>
      <body>
        <h1>Offer List</h1>
        <table>
          <thead>
            <tr>
              <th>Sr No.</th>
              <th>Company</th>
              <th>Company URL</th>
              <th>Title</th>
              <th>Short Description</th>
              <th>Coupon</th>
              <th>Offer Details</th>
              <th>Start Date</th>
              <th>End Date</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
    </html>`;

  const printWindow = window.open("", "_blank");
  printWindow.document.write(html);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 500);
};



  const columns = useMemo(
  () => [
    {
      header: "Actions",
      Cell: ({ row }) => (
       <Box sx={{ display: "flex", gap: 1 }}>
  <Tooltip title="Edit Offer" arrow placement="top">
    <IconButton onClick={() => handleEdit(row.original)}>
      <EditIcon sx={{ color: "#174253" }} />
    </IconButton>
  </Tooltip>

  <Tooltip title="Delete Offer" arrow placement="top">
    <IconButton onClick={() => handleDelete(row.original.offerId)}>
      <DeleteIcon sx={{ color: "#d32f2f" }} />
    </IconButton>
  </Tooltip>
</Box>
      ),
    },
    { accessorKey: "offerId", header: "ID" },
      {
      accessorKey: "fkofferTypeId",
      header: "Offer Type",
      Cell: ({ row }) => {
        const type = offerTypes.find(
          (t) => t.offerTypeId === row.original.fkofferTypeId
        );
        return type ? type.offerTypeName : "-";
      },
    },
    { accessorKey: "companyName", header: "Company" },
     { accessorKey: "companyUrl", header: "Company URL" },
    { accessorKey: "offerTitle", header: "Title" },
    {
  accessorKey: "shortDescription",
  header: "Short Description",
  size: 220,
  Cell: ({ row }) => (
    <span
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: "1.4em",
      }}
      title={row.original.shortDescription}
    >
      {row.original.shortDescription}
    </span>
  ),
},
    { accessorKey: "couponCode", header: "Coupon Code" },
    {
  accessorKey: "offerDetails",
  header: "Offer Details",
  size: 250,
  Cell: ({ row }) => (
    <span
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: "1.4em",
      }}
      title={row.original.offerDetails}
    >
      {row.original.offerDetails}
    </span>
  ),
},

    {
      accessorKey: "startdate",
      header: "Start Date",
      Cell: ({ row }) => formatDate(row.original.startdate),
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      Cell: ({ row }) => formatDate(row.original.endDate),
    },
    {
      accessorKey: "companyLogoUrl",
      header: "Company Logo",
      Cell: ({ row }) =>
        row.original.companyLogoUrl ? (
          <img
            src={row.original.companyLogoUrl}
            style={{ width: 50, height: 50, objectFit: "contain" }}
          />
        ) : (
          "No Logo"
        ),
    },
    {
      accessorKey: "offerImageUrl",
      header: "Offer Image",
      Cell: ({ row }) =>
        row.original.offerImageUrl ? (
          <img
            src={row.original.offerImageUrl}
            style={{ width: 60, height: 60, cursor: "pointer" }}
            onClick={() => handleOpenImage(row.original.offerImageUrl)}
          />
        ) : (
          "No Image"
        ),
    },
  ],
  [offerTypes]
);


  return (
    <Box sx={{ width: "100%", bgcolor: "#ffffff", marginTop: 2, p: { xs: 1, md: 0 }, overflow: "hidden" }}>
        
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pr: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", fontWeight: 700, fontSize: 22, color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", pl: 1.5 }}>
              Offer List
            </Box>
            <Button
              sx={{
                borderRadius: 25,
                background: "linear-gradient(90deg, #0b8793, #174253)",
                color: "white",
                "&:hover": { background: "linear-gradient(90deg, #174253, #0b8793)" },
              }}
              onClick={() => {
                setEditOffer(null);
                setModalVisible(true);
              }}
            >
              Add Offers
            </Button>
          </Box>
    
         
          <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
            <div style={{ position: "relative", width: "100%", marginTop: "10px" }}>
              <div style={{ position: "absolute", top: 10, right: 20, left: 20, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Tooltip title="Download Excel">
                    <IconButton onClick={handleDownloadExcel} sx={{ backgroundColor: "#d4f1d4", color: "#174253", "&:hover": { backgroundColor: "#b5e3b5" } }}>
                      <GetAppIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download PDF">
                    <IconButton onClick={handleDownloadPDF} sx={{ backgroundColor: "#ffe2e2", color: "#d32f2f", "&:hover": { backgroundColor: "#ffcccc" } }}>
                      <PictureAsPdfIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print">
                    <IconButton onClick={handlePrint} sx={{ backgroundColor: "#e3f2fd", color: "#174253", "&:hover": { backgroundColor: "#cde4fa" } }}>
                      <PrintIcon />
                    </IconButton>
                  </Tooltip>
                </div>
                <TextField
                  size="small"
                  placeholder="Search offers..."
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#174253" }} /></InputAdornment> }}
                  sx={{ width: { xs: 200, sm: 250 }, "& .MuiOutlinedInput-root": { borderRadius: 25, height: 38, backgroundColor: "#fff", boxShadow: "0 0 6px rgba(0,0,0,0.1)", "& fieldset": { borderColor: "#d0d7de" }, "&:hover fieldset": { borderColor: "#174253" } } }}
                />
              </div>
            </div>
    
           <MaterialReactTable
  columns={columns}
  data={
    [...dataSource] 
      .reverse()   
      .filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(globalFilter.toLowerCase())
      )
  }

  enableColumnActions={false}
  enableDensityToggle={false}
  enableFullScreenToggle={false}
  enableColumnFilters={false}
  enableHiding={false}
  enableGlobalFilter={false}

  muiTablePaperProps={{
    sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" },
  }}

  muiTableContainerProps={{
    sx: {
      backgroundColor: "#fff",
      width: "100%",
      overflowX: "auto",
      borderRadius: "12px",
      "&::-webkit-scrollbar": { height: "6px" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#e0e3ea",
        borderRadius: "10px",
      },
    },
  }}

  muiTableHeadCellProps={{
    sx: {
      backgroundColor: "#e0e0e0ff",
      color: "#393e4f",
      fontWeight: 600,
      fontSize: "16px",
      borderBottom: "0.5px solid #e3e5eb",
      borderRight: "none",
      borderLeft: "none",
      textAlign: "left",
      height: "63px",
      paddingTop: "23px",
      marginTop: "15px",
    },
  }}

  muiTableBodyRowProps={{
    sx: {
      backgroundColor: "#fff",
      "&:hover": { backgroundColor: "#eff6fe" },
      "& td": {
        fontSize: { xs: "12px", sm: "14px" },
        p: "8px",
        borderBottom: "0.5px solid #e3e5eb",
        borderRight: "none",
        borderLeft: "none",
        height: "54px",
      },
    },
  }}

  initialState={{
    pagination: {
      pageIndex: 0,
      pageSize: 100, 
    },
  }}
/>

          </Box>
    


{modalVisible && (
  <AddOffersModal
    offer={editOffer}
    onClose={() => setModalVisible(false)}
    refresh={fetchOffers}
  />
)}


      <Modal open={openImageModal} onClose={handleCloseImage}>
        <Fade in={openImageModal}>
          <Paper>
            <IconButton onClick={handleCloseImage}>
              <CloseIcon />
            </IconButton>
            <img src={selectedImage} style={{ maxWidth: "90vw" }} />
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default OfferList;
